<template>
  <div class="flex h-full flex-col gap-6 p-1">
    <!-- Filters -->
    <div class="flex items-center justify-between">
      <h2 class="text-lg font-medium text-gray-900 dark:text-white">
        {{ i18n.t('overview') }}
      </h2>
      <div class="flex items-center gap-2">
        <SmSelect
          v-model:selection="selectedDeploymentOption"
          :options="deploymentOptions"
          :placeholder="i18n.t('browserExtension.allDeployments')"
          class="w-64">
          <template #item="{ item }">
            <span
              :class="{
                'italic text-gray-500 dark:text-gray-400': item.value === 'all',
              }">
              {{ item.label }}
            </span>
          </template>
        </SmSelect>
      </div>
    </div>

    <!-- KPI Cards -->
    <div class="grid grid-cols-1 gap-6 md:grid-cols-3">
      <KpiCard
        :title="i18n.t('browserExtension.activeUsers')"
        :value="overview?.total_active_installations || 0"
        :loading="loading"
        :icon="Users" />
      <KpiCard
        :title="i18n.t('browserExtension.activeDeployments')"
        :value="overview?.total_deployments || 0"
        :loading="loading"
        :icon="Building" />
      <KpiCard
        :title="i18n.t('browserExtension.activeTools')"
        :value="overview?.total_active_tools || 0"
        :loading="loading"
        :icon="AppWindow" />
    </div>

    <!-- Charts Row -->
    <div class="grid h-full grid-cols-1 gap-6">
      <div class="min-h-[350px]">
        <TopToolsChart :deployment-id="selectedDeploymentId" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { onMounted, onUnmounted, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useDebounceFn } from '@vueuse/core'
  import { Users, Building, AppWindow } from 'lucide-vue-next'
  import {
    BrowserExtensionManagementService,
    DashboardOverview,
  } from '@/client'
  import { useDeploymentOptions } from '@/stores/browserextension/browserextension.store'
  import { FILTER_DEBOUNCE_MS } from '@/stores/browserextension/constants'
  import KpiCard from './widgets/KpiCard.vue'
  import TopToolsChart from './widgets/TopToolsChart.vue'

  const i18n = useI18n()
  const loading = ref(true)
  const overview = ref<DashboardOverview | null>(null)

  // Use shared deployment options composable
  const {
    deploymentOptions,
    selectedDeploymentOption,
    selectedDeploymentId,
    unsubscribe,
  } = useDeploymentOptions()

  // Cleanup subscriptions on unmount to prevent memory leaks
  onUnmounted(() => {
    unsubscribe()
  })

  async function loadOverview() {
    try {
      loading.value = true
      overview.value =
        await BrowserExtensionManagementService.getDashboardOverviewApiV1BrowserAnalyticsDashboardGet(
          {
            deploymentId: selectedDeploymentId.value,
          }
        )
    } catch (e) {
      console.error('Failed to load dashboard overview', e)
    } finally {
      loading.value = false
    }
  }

  // Use debounce to prevent rapid API calls
  const debouncedLoadOverview = useDebounceFn(loadOverview, FILTER_DEBOUNCE_MS)

  onMounted(() => {
    loadOverview()
  })

  watch(selectedDeploymentId, () => {
    debouncedLoadOverview()
  })
</script>
